import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { renameSociety, resetJoinCode, setCodeSignup, deleteSociety,
         listInvites, inviteEmail, withdrawInvite } from "../lib/api";
import { Field, Notice, PageHead } from "../components/ui";
import Back from "../components/Back";
import CopyBox from "../components/CopyBox";

/**
 * Everything about the society itself. Admin only — the database enforces
 * that too, so a player reaching this page by typing the address gets
 * refusals rather than a working form.
 */
export default function SocietySettings() {
  const { society, isAdmin, refresh, leaveSociety } = useSession();
  const nav = useNavigate();

  const [name, setName] = useState(society.name);
  const [openCode, setOpenCode] = useState(society.allow_code_signup ?? false);
  const [invites, setInvites] = useState([]);
  const [inviting, setInviting] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    setName(society.name);
    setOpenCode(society.allow_code_signup ?? false);
    if (isAdmin) listInvites(society.id).then(setInvites).catch(() => {});
  }, [society, isAdmin]);

  if (!isAdmin) {
    return <Notice kind="error">Only an admin can change the society's settings.</Notice>;
  }

  const run = async (fn, ok) => {
    setError(""); setDone(""); setBusy(true);
    try { await fn(); await refresh(); if (ok) setDone(ok); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Back to="/society">Society</Back>
      <PageHead title="Society settings" sub={society.name} />

      <Notice kind="error">{error}</Notice>
      <Notice kind="ok">{done}</Notice>

      <form className="surface" style={{ padding: "1.1rem", marginBottom: "1.25rem", maxWidth: 460 }}
            onSubmit={(e) => { e.preventDefault();
              run(() => renameSociety(society.id, name), "Society renamed."); }}>
        <h2 style={{ fontSize: "1rem", marginBottom: ".8rem" }}>Name</h2>
        <Field label="Society name" hint="Shown to everyone, everywhere. Change it as often as you like.">
          <input className="input" required value={name}
                 onChange={(e) => setName(e.target.value)} />
        </Field>
        <button className="btn btn-primary" disabled={busy || name.trim() === society.name}>
          {busy ? "Saving…" : "Save name"}
        </button>
      </form>

      <div className="surface" style={{ padding: "1.1rem", marginBottom: "1.25rem", maxWidth: 460 }}>
        <h2 style={{ fontSize: "1rem", marginBottom: ".5rem" }}>Joining</h2>
        <div className="joincode" style={{ marginBottom: ".9rem" }}>
          <div>
            <span className="eyebrow">Society code</span>
            <b>{society.join_code}</b>
          </div>
          <p className="muted">Give this to members so they can join themselves.</p>
          <button className="btn-link" disabled={busy} onClick={() => {
            if (!window.confirm("Change the code? The old one stops working.")) return;
            run(() => resetJoinCode(society.id), "New code generated.");
          }}>New code</button>
        </div>

        <label className="row" style={{ cursor: "pointer", marginBottom: ".9rem" }}>
          <input type="checkbox" checked={openCode} disabled={busy}
                 onChange={(e) => {
                   const v = e.target.checked;
                   setOpenCode(v);
                   run(() => setCodeSignup(society.id, v)).catch(() => setOpenCode(!v));
                 }} />
          <span style={{ fontSize: ".875rem" }}>
            Let anyone with the code create an account
            <span className="muted" style={{ display: "block", fontSize: ".75rem" }}>
              Off by default. With it off, only people you've added can sign up.
            </span>
          </span>
        </label>

        <form className="row" style={{ gap: ".5rem", flexWrap: "wrap" }}
              onSubmit={(e) => { e.preventDefault();
                run(async () => {
                  await inviteEmail(society.id, inviting);
                  setInviting("");
                  setInvites(await listInvites(society.id));
                }, "Invited."); }}>
          <input className="input" type="email" required value={inviting}
                 autoCapitalize="off" spellCheck="false"
                 onChange={(e) => setInviting(e.target.value)}
                 placeholder="email address"
                 style={{ flex: "1 1 14rem", width: "auto" }} />
          <button className="btn btn-ghost" disabled={busy || !inviting}>
            Allow this address
          </button>
        </form>
        <p className="muted" style={{ fontSize: ".75rem", marginTop: ".5rem", lineHeight: 1.55 }}>
          For someone not on the roster yet. <strong>No email is sent</strong> —
          this permits that address to create an account. Tell them yourself
          with the message below.
        </p>

        <h3 className="eyebrow" style={{ margin: "1.2rem 0 .5rem" }}>Message to send them</h3>
        <CopyBox text={
`You're set up on PostcardGolf for ${society.name}.

Go to postcardgolf.app and choose "New here? Create an account".
Use this email address — it's the one we have for you.

Society code: ${society.join_code}

That's it. You'll see the fixtures and can enter your own cards.`} />

        {invites.length > 0 && (
          <div className="list" style={{ marginTop: ".8rem" }}>
            {invites.map((i) => (
              <div className="list-row" key={i.email}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis",
                               whiteSpace: "nowrap", fontSize: ".8125rem" }}>{i.email}</span>
                <button className="btn-link" style={{ color: "var(--dim)" }}
                        onClick={() => run(async () => {
                          await withdrawInvite(society.id, i.email);
                          setInvites(await listInvites(society.id));
                        })}>Withdraw</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="surface danger-zone" style={{ padding: "1.1rem", maxWidth: 460 }}>
        <h2 style={{ fontSize: "1rem", marginBottom: ".4rem" }}>Delete this society</h2>
        <p className="muted" style={{ fontSize: ".8125rem", lineHeight: 1.6, margin: "0 0 .9rem" }}>
          Every competition, round, card and score goes with it, for everyone.
          There is no undo and no export. Type <strong>{society.name}</strong> to
          confirm you mean it.
        </p>
        <Field label="Society name">
          <input className="input" value={confirmName}
                 onChange={(e) => setConfirmName(e.target.value)}
                 placeholder={society.name} />
        </Field>
        <button className="btn-danger" disabled={busy || confirmName.trim() !== society.name}
                onClick={async () => {
                  if (!window.confirm(`Permanently delete ${society.name} and everything in it?`)) return;
                  setBusy(true);
                  try {
                    await deleteSociety(society.id, confirmName);
                    await refresh();
                    leaveSociety();
                    nav("/");
                  } catch (e) { setError(e.message); setBusy(false); }
                }}>
          Delete {society.name}
        </button>
      </div>
    </>
  );
}
